import React, { useCallback, useState } from 'react';
import { Alert, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useMutation } from '@apollo/client';

import { Text } from '../../../components';
import { ActionDock, Screen } from '../../../design/components';
import SetupSection from '../components/SetupSection';
import SetupDateField from '../components/SetupDateField';
import SetupAssigneePicker from '../components/SetupAssigneePicker';
import SetupInfoRow from '../components/SetupInfoRow';
import { LocationSelector, PersonSelector } from '../../../components/selector';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { CREATE_LOCATION_SETUP } from '../../../../common/graphql/locationSetup';

import { useUserContext } from '../../../../common/utils/context';

const LocationSetupCreator = () => {
	const styles = useThemedStyles(createStyles);
	const navigation = useNavigation();
	const { user } = useUserContext();
	const [location, setLocation] = useState(null);
	const [deliveryContact, setDeliveryContact] = useState(null);
	const [dueDate, setDueDate] = useState(new Date());
	const [subscribers, setSubscribers] = useState(user ? [{ id: user.id, firstname: user.firstname, lastname: user.lastname, username: user.username }] : []);
	const [createLocationSetup, { loading }] = useMutation(CREATE_LOCATION_SETUP);


	const handleSubmit = useCallback(async () => {
		if (!location?.id) {
			Alert.alert('Select location', 'Please select a location before creating the setup.');
			return;
		}
		if (!deliveryContact?.id) {
			Alert.alert('Select delivery contact', 'Please select a delivery contact before creating the setup.');
			return;
		}

		try {
			const result = await createLocationSetup({
				variables: {
					input: {
						location: location.id,
						deliveryContact: deliveryContact.id,
						dueDate,
						subscribers: subscribers.map(user => user.id),
					},
				},
			});

			const createdSetup = result?.data?.createLocationSetup;
			if (createdSetup?.id) {
				navigation.replace('LocationSetupDetailView', { locationSetupId: createdSetup.id });
			}
			else {
				navigation.goBack();
			}
		}
		catch (error) {
			Alert.alert('Unable to create', 'Something went wrong while creating the location setup.');
		}
	}, [createLocationSetup, deliveryContact, location, dueDate, subscribers, navigation]);

	return (
		<Screen
			heading="Create location setup"
			subheading={location?.name || 'Select a location to continue'}
			dock={(
				<ActionDock
					action={{
						label: 'Create location setup',
						onPress: handleSubmit,
						loading,
					}}
				/>
			)}
		>
			<SetupSection title="Location setup information">
				<Text style={styles.label}>Location</Text>
				<LocationSelector
					value={location}
					onChange={(selectedLocation) => setLocation(selectedLocation)}
					buttonStyle={styles.selectorButton}
				/>
				<Text style={styles.label}>
					Delivery contact<Text style={styles.required}> *</Text>
				</Text>
				<PersonSelector
					value={deliveryContact}
					onChange={setDeliveryContact}
					buttonStyle={styles.selectorButton}
				/>
				{deliveryContact && (
					<View>
						<SetupInfoRow label="Name" value={deliveryContact.name || 'Not set'} />
						<SetupInfoRow label="Phone" value={deliveryContact.phoneNumber || 'Not set'} />
						<SetupInfoRow label="Email" value={deliveryContact.email || 'Not set'} />
						<SetupInfoRow label="Role" value={deliveryContact.role || 'Not set'} />
					</View>
				)}
				<SetupDateField
					label="Due date"
					value={dueDate}
					onChange={setDueDate}
				/>
				<Text style={styles.label}>Subscribers</Text>
				<SetupAssigneePicker
					assignees={subscribers}
					onChange={setSubscribers}
					placeholder="Add subscriber"
					returnObjects={true}
				/>
			</SetupSection>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	label: {
		...type.fieldLabel,
		color: colors.text,
		marginTop: space.md,
		marginBottom: 6,
	},
	required: {
		color: colors.danger,
	},
	selectorButton: {
		marginBottom: space.sm,
	},
});

export default LocationSetupCreator;
